import { Button, Icon, type ServerState } from '@uplink/ui';
import { useEffect, useState } from 'react';
import type { Messages } from './i18n/ru.ts';
import { usePrefs } from './prefs.tsx';
import { haptic } from './telegram.ts';

export type Region = 'europe' | 'americas' | 'asia' | 'cis';

export const REGIONS: Region[] = ['europe', 'americas', 'asia', 'cis'];

const REGION_OF: Record<string, Region> = {
  NL: 'europe',
  DE: 'europe',
  GB: 'europe',
  FR: 'europe',
  SE: 'europe',
  PL: 'europe',
  RO: 'europe',
  IT: 'europe',
  ES: 'europe',
  FI: 'europe',
  CH: 'europe',
  AT: 'europe',
  US: 'americas',
  CA: 'americas',
  MX: 'americas',
  BR: 'americas',
  CL: 'americas',
  SG: 'asia',
  JP: 'asia',
  KR: 'asia',
  IN: 'asia',
  ID: 'asia',
  AU: 'asia',
  HK: 'asia',
  IL: 'asia',
  RU: 'cis',
  KZ: 'cis',
  BY: 'cis',
  AM: 'cis',
  GE: 'cis',
  UZ: 'cis',
};

export function regionOf(country: string): Region {
  return REGION_OF[country] ?? 'europe';
}

/** Server status from the API → the signal meter state and a plain label. */
export function serverState(
  s: { status: string; powerState: string | null },
  t: Messages,
): {
  state: ServerState;
  label: string;
} {
  const l = t.servers.state;
  if (s.status === 'pending' || s.status === 'provisioning')
    return { state: 'provisioning', label: l.creating };
  if (s.status === 'failed') return { state: 'error', label: l.failed };
  if (s.status === 'suspended') return { state: 'error', label: l.suspended };
  if (s.status === 'deleted') return { state: 'off', label: l.deleted };
  switch (s.powerState) {
    case 'running':
      return { state: 'ok', label: l.running };
    case 'stopped':
      return { state: 'off', label: l.stopped };
    case 'provisioning':
      return { state: 'provisioning', label: l.reinstalling };
    case 'blocked':
      return { state: 'error', label: l.blocked };
    default:
      return { state: 'busy', label: l.busy };
  }
}

/** "1024" → "1 ГБ" */
export function ram(mb: number, t: Messages): string {
  return mb >= 1024 ? `${+(mb / 1024).toFixed(1)} ${t.servers.gb}` : `${mb} ${t.servers.mb}`;
}

/**
 * A destructive action that asks twice: the first tap arms it for a few seconds and says what will
 * happen, the second does it.
 */
export function ConfirmButton({
  children,
  confirmText,
  onConfirm,
  loading,
  variant = 'destructive',
}: {
  children: string;
  confirmText: string;
  onConfirm: () => void;
  loading?: boolean;
  variant?: 'destructive' | 'secondary';
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Button
      variant={variant}
      block
      loading={loading}
      onClick={() => {
        if (!armed) {
          haptic.tap();
          setArmed(true);
          return;
        }
        setArmed(false);
        onConfirm();
      }}
    >
      {armed ? confirmText : children}
    </Button>
  );
}

/** Copy button for a row; `label` empty = icon only, with the text as its accessible name. */
export function CopyText({ text, label }: { text: string; label?: string }) {
  const { t } = usePrefs();
  const [done, setDone] = useState(false);
  const shown = label ?? t.common.copy;
  return (
    <button
      type="button"
      className={`ul-copy${done ? ' is-done' : ''}`}
      aria-label={shown ? undefined : `${t.common.copy} ${text}`}
      onClick={() =>
        navigator.clipboard?.writeText(text).then(() => {
          haptic.success();
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        })
      }
    >
      <Icon name={done ? 'check' : 'copy'} size={16} />
      {done ? (shown ? t.common.copied : null) : shown}
    </button>
  );
}
